import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { User } from '../../types/user';
import { useCreateApprovalHistoryMutation, useUpdateApprovalRequestMutation } from '../../hooks/approval-request-hooks';
import {
  ApprovalRequest,
  ApprovalRequestStatusLevel,
  ApprovalRequestType,
  RequestHistoryPayload,
  ApprovalRequestPayload,
  ApprovalRequestSource,
  EscalationLevel,
} from '../../types/approval-request';
import {
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import { putItem } from '../../support/queries';
import { CacheKey } from '../../types/cache-keys';
import { useSelector } from 'react-redux';
import { RootState } from '../../support/store';
import StbButton, { StbButtonRole } from '../security-toolbox-ui/StbButton';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { StbModalRole } from '../security-toolbox-ui/StbModal';
import { useFetchEscalationRequest, useUpdateEscalationMutation } from '../../hooks/escalation-request-hooks';
import EscalationAssignmentModal from './EscalationAssignmentModal';
import { OpportunityProduct } from '../../types/opportunity';


export const ACTION = {
  [ApprovalRequestStatusLevel.Approved]: 'approve',
  [ApprovalRequestStatusLevel.Denied]: 'deny',
  [ApprovalRequestStatusLevel.UpdateRequired]: 'request further justification for',
  [ApprovalRequestStatusLevel.Cancelled]: 'cancel',
  [ApprovalRequestStatusLevel.Pending]: 'resend',
}

export const MODAL_ROLE = {
  [ApprovalRequestStatusLevel.Approved]: 'default',
  [ApprovalRequestStatusLevel.Denied]: 'default',
  [ApprovalRequestStatusLevel.UpdateRequired]: 'update',
  [ApprovalRequestStatusLevel.Cancelled]: 'delete',
  [ApprovalRequestStatusLevel.Pending]: 'default',
};

export const BUTTON_ROLE = {
  [ApprovalRequestStatusLevel.Approved]: 'confirm',
  [ApprovalRequestStatusLevel.Denied]: 'deny',
  [ApprovalRequestStatusLevel.UpdateRequired]: 'default',
  [ApprovalRequestStatusLevel.Cancelled]: 'default',
  [ApprovalRequestStatusLevel.Pending]: 'request',
};

export const SUBMIT_LABEL = {
  [ApprovalRequestStatusLevel.Approved]: 'Approve Request',
  [ApprovalRequestStatusLevel.Denied]: 'Deny Request',
  [ApprovalRequestStatusLevel.UpdateRequired]: 'Request Further Justification',
  [ApprovalRequestStatusLevel.Cancelled]: 'Cancel Request',
  [ApprovalRequestStatusLevel.Pending]: 'Resend Request',
};

export const SUCCESS_MESSAGE = {
  [ApprovalRequestStatusLevel.Approved]: 'Request approved',
  [ApprovalRequestStatusLevel.Denied]: 'Request denied',
  [ApprovalRequestStatusLevel.UpdateRequired]: 'Further justification requested',
  [ApprovalRequestStatusLevel.Cancelled]: 'Request cancelled',
  [ApprovalRequestStatusLevel.Pending]: 'Request resent',
};

const ApprovalListItemActions = ({ request, managerView }: { request: ApprovalRequest, managerView: boolean }) => {
  const queryClient = useQueryClient();

  const { id: userId } = useSelector((state: { user: { userData: User } }) => state.user.userData);
  const opportunityId = request.requested_item?.[ApprovalRequestSource.Opportunity] ?? '';

  const { mutateAsync: updateApprovalRequest } = useUpdateApprovalRequestMutation(request.requested_item);
  const { mutateAsync: createApprovalHistory } = useCreateApprovalHistoryMutation();
  const { mutateAsync: updateEscalation } = useUpdateEscalationMutation(opportunityId);

  const { navigationViews } = useSelector((state: RootState) => state.user);
  const isAdmin = navigationViews.includes('admin');
  const { data: escalation, isLoading: loadingEscalation } = useFetchEscalationRequest(
    opportunityId,
    request.requested_item?.escalation ?? '',
    {},
    { enabled: request.requested_item?.requested_item_type === ApprovalRequestType.Escalation }
  );

  const [action, setAction] = React.useState<ApprovalRequestStatusLevel | null>(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [openAssignModal, setOpenAssignModal] = React.useState(false);
  const [additionalNote, setAdditionalNote] = React.useState<string | null>(null);
  const [additionalNoteTouched, setAdditionalNoteTouched] = React.useState(false);
  useEffect(() => {
    if (openModal && action === ApprovalRequestStatusLevel.Pending) {
      setAdditionalNote(request?.justification ?? '');
    }
  }, [openModal, request, action]);

  const managerAction = [ApprovalRequestStatusLevel.Approved, ApprovalRequestStatusLevel.Denied, ApprovalRequestStatusLevel.UpdateRequired].includes(action);
  const isNoteRequired = !managerAction && action === ApprovalRequestStatusLevel.Pending && request.requested_item_type === ApprovalRequestType.Escalation;
  const submitDisabled = isNoteRequired && (additionalNote ?? '').trim() === '';
  
  const groupClassName = (actionType) => {
    switch (actionType) {
      case 'approve':
        return 'hbr-button-group__button--first';
      case 'deny':
        return !isAdmin && request.status.id === ApprovalRequestStatusLevel.UpdateRequired
          ? 'hbr-button-group__button--last'
          : 'hbr-button-group__button--inner';
      case 'updateRequired':
        return isAdmin
          ? 'hbr-button-group__button--inner'
          : 'hbr-button-group__button--last';
      case 'cancel':
        return isAdmin
          ? (request.status.id === ApprovalRequestStatusLevel.UpdateRequired
            ? "hbr-button-group__button--inner"
            : "hbr-button-group__button--last")
          : (request.status.id === ApprovalRequestStatusLevel.UpdateRequired
            ? "hbr-button-group__button--first"
            : "hbr-button-group__button--first hbr-button-group__button--last");
      case 'request':
        return 'hbr-button-group__button--last';
      default: ''
    }
  }

  const postResponseFunction = () => {
    switch (request.requested_item_type) {
      case ApprovalRequestType.PoV: {
        return async () => {
          if (action !== ApprovalRequestStatusLevel.Approved) return;
          try {
            const opportunityProduct = request?.requested_item?.opportunity_product ?? null;
            if (!opportunityProduct) {
              throw new Error('No opportunity product found for this PoV request.');
            }

            const payload = {
              ...opportunityProduct,
              sem_approved: true,
            };
            await putItem('db/opportunity-product', payload);
            queryClient.invalidateQueries({ queryKey: [CacheKey.OpportunityProductAssignment, payload.opportunity, payload.id].map(String), refetchType: 'all' });
          } catch (error) {
            throw new Error('Error updating PoV');
          }
        };
      }
      default: {
        return async () => {};
      }
    }
  }

  const closeModal = async () => {
    setAdditionalNote(null);
    setAdditionalNoteTouched(false);
    setOpenModal(false);
  }

  const closeAssignModal = async () => {
    setOpenAssignModal(false);
  }

  const updateRequest = async () => {
    const note = (additionalNote ?? '').trim();

    const payload: Omit<ApprovalRequestPayload, 'requested_item_id'> = {
      id: request?.id,
      requestor_id: request?.requestor?.id,
      approver_id: request?.approver?.id,
      requested_item_type: request?.requested_item_type,
      ...(note !== ''
        ? (managerAction
          ? { approver_note: note }
          : { justification: note })
        : {}),
      status_id: action,
    };
    const history: RequestHistoryPayload = {
      approval_request_id: request?.id,
      previous_status_id: request?.status.id ?? null,
      current_status_id: action,
      ...(note !== ''
        ? (managerAction
          ? {
              current_approver_note: note,
              previous_approver_note: request?.approver_note ?? null
            }
          : {
              current_justification: note,
              previous_justification: request?.justification ?? null
            })
        : {}
      ),
      action_by_user_id: userId,
    };
    await updateApprovalRequest(payload);
    await createApprovalHistory(history);

    if (request?.requested_item_type === ApprovalRequestType.Escalation) {
      if (
        escalation?.stage === EscalationLevel.Domain &&
        [ApprovalRequestStatusLevel.Cancelled, ApprovalRequestStatusLevel.Denied, ApprovalRequestStatusLevel.Pending].includes(action)
      ) {
        const escalationUpdate = {
          id: escalation?.id,
          description: action === ApprovalRequestStatusLevel.Pending && !managerAction && note !== ''
            ? note
            : escalation?.description ?? '',
          status_id: action,
        }
        await updateEscalation(escalationUpdate);
      }
    }
    
    await postResponseFunction()()
  };

  return (
    <>
      {(managerView || isAdmin) && (
        <>
          {[ApprovalRequestStatusLevel.Pending, ApprovalRequestStatusLevel.UpdateRequired].includes(request.status.id) && (
            <StbButton
              onClick={() => {
                setAction(ApprovalRequestStatusLevel.Approved);
                if (request.requested_item_type === ApprovalRequestType.Escalation) {
                  setOpenAssignModal(true);
                } else {
                  setOpenModal(true);
                }
              }}
              className={`hbr-button-group__button ${groupClassName('approve')}`}
            >
              {request.requested_item_type === ApprovalRequestType.Escalation ? 'Assign' : 'Approve'}
            </StbButton>
          )}
          {[ApprovalRequestStatusLevel.Pending, ApprovalRequestStatusLevel.UpdateRequired].includes(request.status.id) && (
            <StbButton
              onClick={() => {
                setAction(ApprovalRequestStatusLevel.Denied);
                setOpenModal(true);
              }}
              className={`hbr-button-group__button ${groupClassName('deny')}`}
            >
              Deny
            </StbButton>
          )}
          {request.status.id === ApprovalRequestStatusLevel.Pending && (
            <StbButton
              onClick={() => {
                setAction(ApprovalRequestStatusLevel.UpdateRequired);
                setOpenModal(true);
              }}
              className={`hbr-button-group__button ${groupClassName('updateRequired')}`}
            >
              Request More Information
            </StbButton>
          )}
        </>
      )}
      {(!managerView || isAdmin) && (
        <>
          {[ApprovalRequestStatusLevel.Pending, ApprovalRequestStatusLevel.UpdateRequired].includes(request.status.id) && (
            <StbButton
              onClick={() => {
                setAction(ApprovalRequestStatusLevel.Cancelled);
                setOpenModal(true);
              }}
              className={`hbr-button-group__button ${groupClassName('cancel')}`}
            >
              Cancel
            </StbButton>
          )}
          {request.status.id === ApprovalRequestStatusLevel.UpdateRequired && (
            <StbButton
              onClick={() => {
                setAction(ApprovalRequestStatusLevel.Pending);
                setOpenModal(true);
              }}
              className={`hbr-button-group__button ${groupClassName('request')}`}
            >
              Resend Request
            </StbButton>
          )}
        </>
      )}

      {
        managerView &&
        (request.status.id === ApprovalRequestStatusLevel.Pending || request.status.id === ApprovalRequestStatusLevel.UpdateRequired) &&
        request.requested_item_type === ApprovalRequestType.Escalation &&
        (
          <EscalationAssignmentModal request={request} open={openAssignModal} closeModal={closeAssignModal} />
        )
      }

      <StbApiModal
        open={openModal}
        title={`${SUBMIT_LABEL[action] ?? 'Update Request'}`}
        role={MODAL_ROLE[action] as StbModalRole}
        submitButtonRole={BUTTON_ROLE[action] as StbButtonRole}
        submitLabel={SUBMIT_LABEL[action] ?? 'Update Request'}
        submitSuccess={SUCCESS_MESSAGE[action] ?? 'Request updated successfully'}
        submitDisabled={submitDisabled}
        onSubmit={updateRequest}
        onClose={closeModal}
      >
        <>
          <div className="mb-3">Are you sure you want to {ACTION[action] ?? 'update'} this request?</div>
          { action !== ApprovalRequestStatusLevel.Cancelled && (
            <HbrTextarea
              value={additionalNote ?? ''}
              onHbr-input={e => {
                setAdditionalNote(e.target.value);
                setAdditionalNoteTouched(true);
              }}
              placeholder="Add additional notes..."
              rows={3}
              error={isNoteRequired && additionalNoteTouched && (additionalNote ?? '').trim() === ''}
            >
              <div slot="label">{managerAction ? "Approver Note" : "Further details"}{isNoteRequired && <em className="text-gray-500"> (required)</em>}</div>
              {isNoteRequired && additionalNoteTouched && (additionalNote ?? '').trim() === '' && (
                <div slot="help-text" className="text-sm mt-2 text-danger">
                  Details are required to support your request
                </div>
              )}
            </HbrTextarea>
          )}
        </>
      </StbApiModal>
    </>
  );
};

export default ApprovalListItemActions;
